import React, { Children, isValidElement, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * A <select> that works with a TV remote. The TV app's OK button calls .click() on the focused element, and .click()
 * cannot open a native drop-down, so on a TV this is a button that opens a list: Up / Down choose, OK picks, Back closes.
 * Everywhere else it is a plain <select>. Same props and <option> children; onChange gets { target: { value } }.
 */
function isTvRemote() {
  if (typeof document === 'undefined') return false;
  return /ZFLIX-TV/.test(navigator.userAgent) || document.documentElement.classList.contains('zflix-tv-apk-density');
}

function readOptions(children) {
  const out = [];
  Children.toArray(children).forEach((el) => {
    if (!isValidElement(el)) return;
    if (el.type === 'option') out.push({ value: String(el.props.value ?? ''), label: Children.toArray(el.props.children).join('') });
    else if (el.props?.children) out.push(...readOptions(el.props.children));
  });
  return out;
}

function PickerList({ label, options, value, onPick, onClose, returnFocusTo }) {
  const [idx, setIdx] = useState(() => Math.max(0, options.findIndex((o) => o.value === String(value ?? ''))));
  const idxRef = useRef(idx);
  idxRef.current = idx;
  const rowsRef = useRef([]);

  useEffect(() => {
    const el = rowsRef.current[idx];
    if (!el) return;
    el.focus({ preventScroll: true });
    el.scrollIntoView({ block: 'nearest' });
  }, [idx]);

  useEffect(() => {
    /** tvSpatialNav hands every key to __zflixTopKeys first; Back goes through __zflixOwnBack (same as the record menu). */
    const prevKeys = window.__zflixTopKeys;
    const prevBack = window.__zflixOwnBack;
    window.__zflixOwnBack = () => {
      onClose();
      return true;
    };
    window.__zflixTopKeys = (e) => {
      const k = e.key;
      const code = e.keyCode;
      if (k === 'ArrowUp' || code === 38) setIdx((i) => Math.max(0, i - 1));
      else if (k === 'ArrowDown' || code === 40) setIdx((i) => Math.min(options.length - 1, i + 1));
      else if (k === 'Enter' || code === 13) onPick(options[idxRef.current]);
      else if (k === 'Escape' || code === 27) onClose();
      return true;
    };
    return () => {
      if (prevKeys) window.__zflixTopKeys = prevKeys;
      else delete window.__zflixTopKeys;
      if (prevBack) window.__zflixOwnBack = prevBack;
      else delete window.__zflixOwnBack;
      returnFocusTo.current?.focus({ preventScroll: true });
    };
  }, []);

  return createPortal(
    <div
      role="presentation"
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, zIndex: 3000, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={label || 'Choose'}
        onClick={(e) => e.stopPropagation()}
        style={{ minWidth: 280, maxWidth: '70vw', maxHeight: '76vh', overflowY: 'auto', padding: 8, borderRadius: 12, background: 'var(--bg2)', border: '1px solid var(--border)', boxShadow: '0 20px 56px rgba(0,0,0,0.65)' }}
      >
        {label ? <div style={{ padding: '6px 12px 8px', fontSize: 12, letterSpacing: 1, textTransform: 'uppercase', color: 'var(--text2)' }}>{label}</div> : null}
        {options.map((o, i) => (
          <button
            key={`${o.value}-${i}`}
            type="button"
            ref={(el) => {
              rowsRef.current[i] = el;
            }}
            onClick={() => onPick(o)}
            style={{ display: 'flex', gap: 10, width: '100%', padding: '11px 14px', border: 'none', outline: 'none', borderRadius: 6, fontSize: 15, textAlign: 'left', color: '#fff', cursor: 'pointer', background: i === idx ? 'var(--accent)' : 'transparent' }}
          >
            <span style={{ width: 16, flexShrink: 0 }}>{o.value === String(value ?? '') ? '✓' : ''}</span>
            {o.label}
          </button>
        ))}
      </div>
    </div>,
    document.body,
  );
}

export default function TvSelect({ value, onChange, style, children, 'aria-label': ariaLabel, ...rest }) {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef(null);
  if (!isTvRemote()) {
    return (
      <select value={value} onChange={onChange} style={style} aria-label={ariaLabel} {...rest}>
        {children}
      </select>
    );
  }
  const options = readOptions(children);
  const current = options.find((o) => o.value === String(value ?? ''));
  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        id={rest.id}
        className="zflix-tvselect"
        aria-label={ariaLabel}
        aria-haspopup="dialog"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen(true);
        }}
        style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, textAlign: 'left', ...style }}
      >
        <span>{current ? current.label : 'Choose…'}</span>
        <span aria-hidden="true" style={{ opacity: 0.7 }}>▾</span>
      </button>
      {open ? (
        <PickerList
          label={ariaLabel}
          options={options}
          value={value}
          returnFocusTo={buttonRef}
          onClose={() => setOpen(false)}
          onPick={(o) => {
            setOpen(false);
            if (o && o.value !== String(value ?? '')) onChange?.({ target: { value: o.value } });
          }}
        />
      ) : null}
    </>
  );
}
